import React, { useEffect, useState } from "react";
import { Modal, Button, Table, Pagination } from "react-bootstrap";
import { MdFilterAlt, MdFilterAltOff } from "react-icons/md";
import { FaDownload } from "react-icons/fa";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

const API_URL = process.env.REACT_APP_API_URL;
function AssociatesBimaAchieverLists() {
  const [anniversarys, setAnniversary] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [perPage, setPerPage] = useState(100);
  const [searchDate, setSearchDate] = useState(null);
  const [totalRecords, setTotalRecords] = useState(0);
  const [isFilterActive, setIsFilterActive] = useState(false);
  const [filterInput, setFilterInput] = useState("");
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [nameFilter, setNameFilter] = useState("");
  const [mobileFilter, setMobileFilter] = useState("");
  const [parentIdFilter, setParentIdFilter] = useState("");
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [messageModalContent, setMessageModalContent] = useState({
    title: "",
    text: "",
    type: "",
  });

  const showCustomMessageModal = (title, text, type) => {
    setMessageModalContent({ title, text, type });
    setShowMessageModal(true);
  };

  const closeCustomMessageModal = () => {
    setShowMessageModal(false);
    setMessageModalContent({ title: "", text: "", type: "" });
  };

  const getAuthToken = () => {
    return localStorage.getItem("token");
  };

  const formatDateForAPI = (date) => {
    if (!date) return "";
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    return `${day}-${month}`;
  };

  const formatFullDateForAPI = (date) => {
    if (!date) return "";
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const parseDateFromString = (dateStr) => {
    if (!dateStr) return null;
    const formats = [
      /(\d{2})[^\d](\d{2})[^\d](\d{4})/,
      /(\d{2})(\d{2})(\d{4})/,
      /(\d{4})[^\d](\d{2})[^\d](\d{2})/,
    ];

    for (const format of formats) {
      const match = dateStr.match(format);
      if (match) {
        const [, part1, part2, part3] = match;
        if (part3 && part3.length === 4) {
          if (part1.length === 4) {
            return new Date(parseInt(part1), parseInt(part2) - 1, parseInt(part3));
          } else {
            return new Date(parseInt(part3), parseInt(part2) - 1, parseInt(part1));
          }
        } else if (part1.length === 2 && part2.length === 2) {
          return new Date(parseInt(part3), parseInt(part2) - 1, parseInt(part1));
        }
      }
    }

    return null;
  };

  const formatDateForDisplay = (dateStr) => {
    const date = parseDateFromString(dateStr);
    if (!date) return dateStr;
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    return `${month}-${day}`;
  };

  const getMonthDayFromDate = (dateStr) => {
    const date = parseDateFromString(dateStr);
    if (!date) return "";
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    return `${day}-${month}`;
  };

  const validateDateInput = (input) => {
    const regex = /^(\d{2})-(\d{2})$/;
    const match = input.match(regex);
    if (!match) return false;
    const day = parseInt(match[1]);
    const month = parseInt(match[2]);
    if (month < 1 || month > 12) return false;
    if (day < 1 || day > 31) return false;
    const daysInMonth = [31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
    if (day > daysInMonth[month - 1]) return false;

    return true;
  };

  const buildUrlWithFilters = (page = 1, limit = null) => {
    let url = `${API_URL}/user-bima-list?page=${page}`;

    if (limit) {
      url += `&limit=${limit}`;
    } else {
      url += `&limit=${perPage}`;
    }

    if (filterInput && validateDateInput(filterInput)) {
      url += `&date=${filterInput}`;
    } else if (searchDate) {
      url += `&date=${formatDateForAPI(searchDate)}`;
    }

    if (fromDate) {
      const formattedFrom = formatFullDateForAPI(fromDate);
      url += `&from_date=${formattedFrom}`;
    }

    if (toDate) {
      const formattedTo = formatFullDateForAPI(toDate);
      url += `&to_date=${formattedTo}`;
    }
    if (nameFilter && nameFilter.trim()) {
      url += `&name=${encodeURIComponent(nameFilter.trim())}`;
    }

    if (mobileFilter && mobileFilter.trim()) {
      url += `&mobile=${encodeURIComponent(mobileFilter.trim())}`;
    }

    if (parentIdFilter && parentIdFilter.trim()) {
      url += `&parent_id=${encodeURIComponent(parentIdFilter.trim())}`;
    }

    return url;
  };

  const fetchAnniversaryList = async (page = 1) => {
    setLoading(true);
    setError(null);

    try {
      const token = getAuthToken();
      if (!token) {
        showCustomMessageModal(
          "Authentication Error",
          "Authentication token not found. Please log in.",
          "error"
        );
        throw new Error("Authentication token not found. Please log in.");
      }

      const url = buildUrlWithFilters(page);
      console.log("Fetching URL:", url);
      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        if (response.status === 401) {
          showCustomMessageModal(
            "Authorization Error",
            "Unauthorized: Please log in again.",
            "error"
          );
          throw new Error("Unauthorized: Please log in again.");
        }
        const errorData = await response.json();
        showCustomMessageModal(
          "Error",
          errorData.message || "Failed to fetch anniversary list.",
          "error"
        );
        throw new Error(errorData.message || "Failed to fetch anniversary list.");
      }
      const data = await response.json();
      console.log("API Response:", data);

      if (data.status === "1") {
        setAnniversary(data.data || []);
        setTotalRecords(data.total || 0);
        setTotalPages(data.totalPages || 1);
        setCurrentPage(data.currentPage || 1);
      } else {
        showCustomMessageModal("Error", data.message || "Failed to fetch anniversary list.", "error");
        setAnniversary([]);
        setTotalPages(1);
        setCurrentPage(1);
        setTotalRecords(0);
      }
    } catch (err) {
      console.error("Fetch anniversary list error:", err);
      setAnniversary([]);
      setTotalPages(1);
      setCurrentPage(1);
      setTotalRecords(0);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadCSV = async () => {
    setDownloading(true);
    try {
      const token = getAuthToken();
      if (!token) {
        showCustomMessageModal(
          "Authentication Error",
          "Authentication token not found. Please log in.",
          "error"
        );
        return;
      }

      let url = `${API_URL}/user-bima-list-download?page=1&limit=10000`;

      if (filterInput && validateDateInput(filterInput)) {
        url += `&date=${filterInput}`;
      } else if (searchDate) {
        url += `&date=${formatDateForAPI(searchDate)}`;
      }

      if (fromDate) {
        const formattedFrom = formatFullDateForAPI(fromDate);
        url += `&from_date=${formattedFrom}`;
      }

      if (toDate) {
        const formattedTo = formatFullDateForAPI(toDate);
        url += `&to_date=${formattedTo}`;
      }

      // Add new filters to download
      if (nameFilter && nameFilter.trim()) {
        url += `&name=${encodeURIComponent(nameFilter.trim())}`;
      }

      if (mobileFilter && mobileFilter.trim()) {
        url += `&mobile=${encodeURIComponent(mobileFilter.trim())}`;
      }

      if (parentIdFilter && parentIdFilter.trim()) {
        url += `&parent_id=${encodeURIComponent(parentIdFilter.trim())}`;
      }

      console.log("Download URL:", url);

      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error("Failed to download data");
      }

      const data = await response.json();

      if (data.status === "1" && data.data && data.data.length > 0) {
        const headers = [
          "Name",
          "Mobile",
          "Parent Name",
          "Parent Mobile",
          "Area Sqyd",
        ];

        const csvRows = [];
        csvRows.push(headers.join(","));

        data.data.forEach((person, index) => {

          const row = [
            `"${(person.username || "-").replace(/"/g, '""')}"`,
            `"${(person.mobile || "-").replace(/"/g, '""')}"`,

            `"${(person.parent_username || "-").replace(/"/g, '""')}"`,
            `"${(person.parent_mobile || "-").replace(/"/g, '""')}"`,
            `"${(person.area || "-").replace(/"/g, '""')}"`,
          ];
          csvRows.push(row.join(","));
        });

        const csvContent = csvRows.join("\n");
        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
        const link = document.createElement("a");
        const downloadUrl = URL.createObjectURL(blob);

        let filterText = "";
        if (filterInput) filterText += `_anniversary_${filterInput}`;
        if (fromDate) filterText += `_from_${formatFullDateForAPI(fromDate)}`;
        if (toDate) filterText += `_to_${formatFullDateForAPI(toDate)}`;
        if (nameFilter) filterText += `_name_${nameFilter}`;
        if (mobileFilter) filterText += `_mobile_${mobileFilter}`;
        if (parentIdFilter) filterText += `_parent_${parentIdFilter}`;

        const filename = `associates_anniversary_list${filterText}_${new Date().toISOString().split('T')[0]}.csv`;
        link.setAttribute("href", downloadUrl);
        link.setAttribute("download", filename);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(downloadUrl);

        showCustomMessageModal(
          "Success",
          `Successfully downloaded ${data.data.length} filtered records!`,
          "success"
        );
      } else {
        showCustomMessageModal(
          "No Data",
          "No records found with current filters to download!",
          "error"
        );
      }
    } catch (err) {
      console.error("Download CSV error:", err);
      showCustomMessageModal(
        "Download Error",
        err.message || "Failed to download CSV file",
        "error"
      );
    } finally {
      setDownloading(false);
    }
  };

  useEffect(() => {
    fetchAnniversaryList(currentPage);
  }, [currentPage]);

  const handlePageChange = (pageNumber) => {
    if (pageNumber >= 1 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
    }
  };

  const handleSearchClick = () => {
    setCurrentPage(1);
    fetchAnniversaryList(1);
  };

  const handleClearSearch = () => {
    setSearchDate(null);
    setFilterInput("");
    setFromDate(null);
    setToDate(null);
    setNameFilter("");
    setMobileFilter("");
    setParentIdFilter("");
    setCurrentPage(1);
    fetchAnniversaryList(1);
  };

  const handleToggle = () => {
    setIsFilterActive(!isFilterActive);
  };

  const handleDateChange = (date) => {
    setSearchDate(date);
    if (date) {
      setFilterInput(formatDateForAPI(date));
    }
  };

  const handleManualInputChange = (e) => {
    const value = e.target.value;
    const cleaned = value.replace(/[^\d-]/g, '');
    if (cleaned.length === 2 && !cleaned.includes('-')) {
      setFilterInput(cleaned + '-');
    } else if (cleaned.length <= 5) {
      setFilterInput(cleaned);
    }
    if (value) {
      setSearchDate(null);
    }
  };

  const CustomDateInput = React.forwardRef(({ value, onClick }, ref) => (
    <div className="input-group">
      <input
        type="text"
        className="form-control"
        value={filterInput}
        onChange={handleManualInputChange}
        placeholder="DD-MM (e.g., 15-02)"
        ref={ref}
      />
      <button
        className="btn btn-outline-secondary"
        type="button"
        onClick={onClick}
      >
        📅
      </button>
    </div>
  ));

  if (loading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: "80vh" }}
      >
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  // return (
  //   <>
  //     <div className="card mt-2">
  //       <div className="card-header">
  //         <div className="d-flex align-items-center justify-content-between">
  //           <div className="titlepage">
  //             <h3>Associates Bima Achiever Lists</h3>
  //           </div>
  //           <div className="d-flex gap-2">
  //             <button
  //               className={`filter-toggle-btn btn border border-1 ${isFilterActive ? "active" : ""}`}
  //               onClick={handleToggle}
  //             >
  //               {isFilterActive ? <MdFilterAltOff /> : <MdFilterAlt />}
  //             </button>
  //           </div>
  //         </div>
  //       </div>

  //       <div className="card-body">
  //         {isFilterActive && (
  //           <div className="mb-3">
  //             <div className="row g-3">
  //               <div className="col-md-3">
  //                 <label className="form-label">From Date</label>
  //                 <DatePicker
  //                   selected={fromDate}
  //                   onChange={(date) => setFromDate(date)}
  //                   dateFormat="yyyy-MM-dd"
  //                   className="form-control"
  //                   placeholderText="Select from date"
  //                   showMonthDropdown
  //                   showYearDropdown
  //                   dropdownMode="select"
  //                 />
  //               </div>

  //               <div className="col-md-3">
  //                 <label className="form-label">To Date</label>
  //                 <DatePicker
  //                   selected={toDate}
  //                   onChange={(date) => setToDate(date)}
  //                   dateFormat="yyyy-MM-dd"
  //                   className="form-control"
  //                   placeholderText="Select to date"
  //                   showMonthDropdown
  //                   showYearDropdown
  //                   dropdownMode="select"
  //                 />
  //               </div>

  //               <div className="col-md-3">
  //                 <label className="form-label">Name</label>
  //                 <input
  //                   type="text"
  //                   className="form-control"
  //                   value={nameFilter}
  //                   onChange={(e) => setNameFilter(e.target.value)}
  //                   placeholder="Search by name"
  //                 />
  //               </div>

  //               <div className="col-md-3">
  //                 <label className="form-label">Mobile</label>
  //                 <input
  //                   type="text"
  //                   className="form-control"
  //                   value={mobileFilter}
  //                   onChange={(e) => setMobileFilter(e.target.value)}
  //                   placeholder="Search by mobile number"
  //                 />
  //               </div>

  //               {/* <div className="col-md-3">
  //                 <label className="form-label">Parent ID</label>
  //                 <input
  //                   type="text"
  //                   className="form-control"
  //                   value={parentIdFilter}
  //                   onChange={(e) => setParentIdFilter(e.target.value)}
  //                   placeholder="Search by parent ID"
  //                 />
  //               </div> */}

  //               <div className="col-md-12">
  //                 <div className="d-flex gap-2">
  //                   <button className="btn btn-primary" onClick={handleSearchClick}>
  //                     Search
  //                   </button>

  //                   <button className="btn btn-secondary" onClick={handleClearSearch}>
  //                     Clear All Filters
  //                   </button>

  //                   {/* <button className="btn btn-success" onClick={handleDownloadCSV} disabled={downloading}>
  //                     <FaDownload className="me-2" />
  //                     {downloading ? "Downloading..." : "Download CSV"}
  //                   </button> */}
  //                 </div>
  //               </div>
  //             </div>
  //           </div>
  //         )}

  //         {/* <div className="mb-3">
  //           <div className="d-flex justify-content-between align-items-center">
  //             <div>
  //               <h5>Total Records: {totalRecords}</h5>
  //             </div>
  //             {(filterInput || fromDate || toDate || nameFilter || mobileFilter || parentIdFilter) && (
  //               <div className="text-info">
  //                 <small>Filtered data showing {totalRecords} records</small>
  //                 <button
  //                   className="btn btn-sm btn-link"
  //                   onClick={handleClearSearch}
  //                 >
  //                   Clear filters
  //                 </button>
  //               </div>
  //             )}
  //           </div>
  //         </div> */}

  //         <div className="table-responsive">
  //           <Table bordered hover>
  //             <thead className="bg-primary text-white">
  //               <tr>
  //                 <th>#</th>
  //                 <th>Name</th>
  //                 <th>Mobile</th>
  //                 <th>Parent ID</th>
  //                 <th>Lead Date</th>
  //                 <th>Lead Customer Name</th>
  //                 <th>Lead Mobile</th>
  //                 <th>Project Name</th>
  //                 <th>Block Name</th>
  //                 <th>Plot Name</th>
  //                 <th>Bonus Amount</th>
  //               </tr>
  //             </thead>
  //             <tbody>
  //               {anniversarys.length > 0 ? (
  //                 anniversarys.map((person, index) => (
  //                   <tr key={person.id || index}>
  //                     <td>{(currentPage - 1) * perPage + index + 1}</td>
  //                     <td>{person.user_name || "-"}</td>
  //                     <td>{person.user_mobile || "-"}</td>
  //                     <td>{person.parent_id || "-"}</td>
  //                     <td>
  //                       {person.status_date
  //                         ? new Date(person.status_date).toLocaleDateString("en-GB")
  //                         : "-"}
  //                     </td>
  //                     <td>{person.customer_name || "-"}</td>
  //                     <td>{person.customer_mobile || "-"}</td>
  //                     <td>{person.name || "-"}</td>
  //                     <td>{person.colony_name || "-"}</td>
  //                     <td>{person.plot_shop_villa_no || "-"}</td>
  //                     <td>{person.gross_payout || "-"}</td>
  //                   </tr>
  //                 ))
  //               ) : (
  //                 <tr>
  //                   <td colSpan="12" className="text-center">
  //                     {(filterInput || searchDate || fromDate || toDate || nameFilter || mobileFilter || parentIdFilter) ?
  //                       `No records found for selected filters` :
  //                       "No records found."}
  //                   </td>
  //                 </tr>
  //               )}
  //             </tbody>
  //           </Table>
  //         </div>

  //         {totalPages > 1 && (
  //           <div className="d-flex justify-content-between align-items-center mt-3 gap-2 flex-wrap-mobile">
  //             <div className="text-muted">
  //               Showing {(currentPage - 1) * perPage + 1} to{" "}
  //               {Math.min(currentPage * perPage, totalRecords)} of {totalRecords} records
  //             </div>
  //             <div>
  //               <Pagination>
  //                 <Pagination.Prev
  //                   onClick={() => handlePageChange(currentPage - 1)}
  //                   disabled={currentPage === 1}
  //                 />

  //                 {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
  //                   let pageNum;
  //                   if (totalPages <= 5) {
  //                     pageNum = i + 1;
  //                   } else if (currentPage <= 3) {
  //                     pageNum = i + 1;
  //                   } else if (currentPage >= totalPages - 2) {
  //                     pageNum = totalPages - 4 + i;
  //                   } else {
  //                     pageNum = currentPage - 2 + i;
  //                   }

  //                   return (
  //                     <Pagination.Item
  //                       key={pageNum}
  //                       active={pageNum === currentPage}
  //                       onClick={() => handlePageChange(pageNum)}
  //                     >
  //                       {pageNum}
  //                     </Pagination.Item>
  //                   );
  //                 })}

  //                 <Pagination.Next
  //                   onClick={() => handlePageChange(currentPage + 1)}
  //                   disabled={currentPage === totalPages}
  //                 />
  //               </Pagination>
  //             </div>
  //           </div>
  //         )}
  //       </div>
  //     </div>

  //     {showMessageModal && (
  //       <div
  //         className="modal d-block"
  //         tabIndex="-1"
  //         style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
  //       >
  //         <div className="modal-dialog modal-dialog-centered">
  //           <div
  //             className={`modal-content ${messageModalContent.type === "success"
  //               ? "border-success"
  //               : "border-danger"}`}
  //           >
  //             <div className="modal-header">
  //               <h5
  //                 className={`modal-title ${messageModalContent.type === "success"
  //                   ? "text-success"
  //                   : "text-danger"}`}
  //               >
  //                 {messageModalContent.title}
  //               </h5>
  //               <button
  //                 type="button"
  //                 className="btn-close"
  //                 onClick={closeCustomMessageModal}
  //               ></button>
  //             </div>
  //             <div className="modal-body">
  //               <p>{messageModalContent.text}</p>
  //             </div>
  //             <div className="modal-footer">
  //               <Button
  //                 variant={messageModalContent.type === "success" ? "success" : "danger"}
  //                 onClick={closeCustomMessageModal}
  //               >
  //                 OK
  //               </Button>
  //             </div>
  //           </div>
  //         </div>
  //       </div>
  //     )}
  //   </>
  // );
return (
    <>
      <div
        style={{
          minHeight: "100vh",
          background: "linear-gradient(135deg, #f0f6ff 0%, #e6f0fa 100%)",
          padding: "30px 24px",
          fontFamily: "'Poppins', 'DM Sans', sans-serif",
        }}
      >
        <div style={{ maxWidth: "1600px", margin: "0 auto" }}>
          {/* 🎯 HEADER CARD */}
          <div
            style={{
              background: "#ffffff",
              borderRadius: "16px",
              boxShadow: "0 8px 24px rgba(30, 87, 153, 0.08)",
              border: "1.5px solid #e2e8f0",
              padding: "20px 24px",
              display: "flex",
              alignItems: "center",
              gap: "16px",
              marginBottom: "20px",
              flexWrap: "wrap",
            }}
          >
            <div
              style={{
                width: "52px",
                height: "52px",
                borderRadius: "14px",
                background: "linear-gradient(135deg, #1e5799, #2b6cb0)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#ffffff",
                fontSize: "24px",
                boxShadow: "0 8px 20px rgba(30, 87, 153, 0.3)",
                flexShrink: 0,
              }}
            >
              🏅
            </div>
            <div style={{ flex: 1, minWidth: "200px" }}>
              <h1
                style={{
                  margin: 0,
                  fontSize: "24px",
                  fontWeight: "800",
                  color: "#1e3a5f",
                  letterSpacing: "-0.5px",
                }}
              >
                Associates Bima Achiever Lists
              </h1>
              <p
                style={{
                  margin: "2px 0 0",
                  fontSize: "13px",
                  color: "#64748b",
                  fontWeight: "500",
                }}
              >
                View and download Bima achiever records
              </p>
            </div>

            <button
              onClick={handleToggle}
              style={{
                padding: "12px 16px",
                borderRadius: "12px",
                border: "1.5px solid #e2e8f0",
                background: isFilterActive ? "#1e5799" : "#ffffff",
                color: isFilterActive ? "#ffffff" : "#1e3a5f",
                fontSize: "16px",
                cursor: "pointer",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
              }}
              title="Toggle Filters"
            >
              {isFilterActive ? <MdFilterAltOff /> : <MdFilterAlt />}
            </button>
          </div>

          {/* 🎯 MAIN CARD */}
          <div
            style={{
              background: "#ffffff",
              borderRadius: "16px",
              boxShadow: "0 8px 24px rgba(30, 87, 153, 0.08)",
              border: "1.5px solid #e2e8f0",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                height: "5px",
                background: "linear-gradient(90deg, #1e5799, #2b6cb0, #1e5799)",
              }}
            />

            <div style={{ padding: "24px" }}>
              {/* 🎯 FILTERS */}
              {isFilterActive && (
                <div
                  style={{
                    display: "flex",
                    gap: "12px",
                    marginBottom: "20px",
                    flexWrap: "wrap",
                    alignItems: "flex-end",
                  }}
                >
                  <div style={{ flex: 1, minWidth: "180px" }}>
                    <label
                      style={{
                        display: "block",
                        fontSize: "12.5px",
                        fontWeight: "700",
                        color: "#1e3a5f",
                        marginBottom: "6px",
                      }}
                    >
                      From Date
                    </label>
                    <DatePicker
                      selected={fromDate}
                      onChange={(date) => setFromDate(date)}
                      dateFormat="yyyy-MM-dd"
                      placeholderText="Select from date"
                      showMonthDropdown
                      showYearDropdown
                      dropdownMode="select"
                      wrapperClassName="w-100"
                      customInput={
                        <input
                          style={{
                            width: "100%",
                            padding: "10px 14px",
                            borderRadius: "10px",
                            border: "1.5px solid #e2e8f0",
                            outline: "none",
                            fontSize: "14px",
                          }}
                        />
                      }
                    />
                  </div>

                  <div style={{ flex: 1, minWidth: "180px" }}>
                    <label
                      style={{
                        display: "block",
                        fontSize: "12.5px",
                        fontWeight: "700",
                        color: "#1e3a5f",
                        marginBottom: "6px",
                      }}
                    >
                      To Date
                    </label>
                    <DatePicker
                      selected={toDate}
                      onChange={(date) => setToDate(date)}
                      dateFormat="yyyy-MM-dd"
                      placeholderText="Select to date"
                      showMonthDropdown
                      showYearDropdown
                      dropdownMode="select"
                      wrapperClassName="w-100"
                      customInput={
                        <input
                          style={{
                            width: "100%",
                            padding: "10px 14px",
                            borderRadius: "10px",
                            border: "1.5px solid #e2e8f0",
                            outline: "none",
                            fontSize: "14px",
                          }}
                        />
                      }
                    />
                  </div>

                  <div style={{ flex: 1, minWidth: "180px" }}>
                    <label
                      style={{
                        display: "block",
                        fontSize: "12.5px",
                        fontWeight: "700",
                        color: "#1e3a5f",
                        marginBottom: "6px",
                      }}
                    >
                      Name
                    </label>
                    <input
                      type="text"
                      value={nameFilter}
                      onChange={(e) => setNameFilter(e.target.value)}
                      placeholder="Search by name"
                      style={{
                        width: "100%",
                        padding: "10px 14px",
                        borderRadius: "10px",
                        border: "1.5px solid #e2e8f0",
                        outline: "none",
                        fontSize: "14px",
                      }}
                    />
                  </div>

                  <div style={{ flex: 1, minWidth: "180px" }}>
                    <label
                      style={{
                        display: "block",
                        fontSize: "12.5px",
                        fontWeight: "700",
                        color: "#1e3a5f",
                        marginBottom: "6px",
                      }}
                    >
                      Mobile
                    </label>
                    <input
                      type="text"
                      value={mobileFilter}
                      onChange={(e) => setMobileFilter(e.target.value)}
                      placeholder="Search by mobile number"
                      style={{
                        width: "100%",
                        padding: "10px 14px",
                        borderRadius: "10px",
                        border: "1.5px solid #e2e8f0",
                        outline: "none",
                        fontSize: "14px",
                      }}
                    />
                  </div>

                  <div style={{ display: "flex", gap: "10px" }}>
                    <button
                      onClick={handleSearchClick}
                      style={{
                        padding: "10px 22px",
                        borderRadius: "10px",
                        border: "none",
                        background:
                          "linear-gradient(135deg, #1e5799, #2b6cb0)",
                        color: "#ffffff",
                        fontWeight: "700",
                        cursor: "pointer",
                        fontSize: "14px",
                      }}
                    >
                      Search
                    </button>

                    <button
                      onClick={handleClearSearch}
                      style={{
                        padding: "10px 22px",
                        borderRadius: "10px",
                        border: "1.5px solid #e2e8f0",
                        background: "#f8fafc",
                        color: "#1e3a5f",
                        fontWeight: "700",
                        cursor: "pointer",
                        fontSize: "14px",
                      }}
                    >
                      Clear
                    </button>
                  </div>
                </div>
              )}

              {/* 🎯 TOTAL RECORDS */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "16px",
                  flexWrap: "wrap",
                  gap: "8px",
                }}
              >
                <h5
                  style={{
                    margin: 0,
                    fontSize: "15px",
                    fontWeight: "700",
                    color: "#1e3a5f",
                  }}
                >
                  Total Records: {totalRecords}
                </h5>
                {(filterInput ||
                  fromDate ||
                  toDate ||
                  nameFilter ||
                  mobileFilter ||
                  parentIdFilter) && (
                  <small
                    style={{
                      color: "#2b6cb0",
                      fontWeight: "600",
                      fontSize: "12.5px",
                    }}
                  >
                    Filtered data showing {totalRecords} records
                  </small>
                )}
              </div>

              <div className="table-responsive">
                <Table bordered hover>
                  <thead
                    style={{
                      background: "linear-gradient(135deg, #1e5799, #2b6cb0)",
                      color: "#ffffff",
                    }}
                  >
                    <tr>
                      <th>#</th>
                      <th>Name</th>
                      <th>Mobile</th>
                      <th>Parent ID</th>
                      <th>Lead Date</th>
                      <th>Lead Customer Name</th>
                      <th>Lead Mobile</th>
                      <th>Project Name</th>
                      <th>Block Name</th>
                      <th>Plot Name</th>
                      <th>Bonus Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {anniversarys.length > 0 ? (
                      anniversarys.map((person, index) => (
                        <tr key={person.id || index}>
                          <td>
                            {(currentPage - 1) * perPage + index + 1}
                          </td>
                          <td>{person.user_name || "-"}</td>
                          <td>{person.user_mobile || "-"}</td>
                          <td>{person.parent_id || "-"}</td>
                          <td>
                            {person.status_date
                              ? new Date(
                                  person.status_date,
                                ).toLocaleDateString("en-GB")
                              : "-"}
                          </td>
                          <td>{person.customer_name || "-"}</td>
                          <td>{person.customer_mobile || "-"}</td>
                          <td>{person.name || "-"}</td>
                          <td>{person.colony_name || "-"}</td>
                          <td>{person.plot_shop_villa_no || "-"}</td>
                          <td>{person.gross_payout || "-"}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="12" className="text-center py-4">
                          {filterInput ||
                          searchDate ||
                          fromDate ||
                          toDate ||
                          nameFilter ||
                          mobileFilter ||
                          parentIdFilter
                            ? `No records found for selected filters`
                            : "No records found."}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>

              {/* 🎯 PAGINATION */}
              {totalPages > 1 && (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginTop: "16px",
                    flexWrap: "wrap",
                    gap: "10px",
                  }}
                >
                  <div
                    style={{
                      color: "#64748b",
                      fontSize: "13px",
                      fontWeight: "500",
                    }}
                  >
                    Showing {(currentPage - 1) * perPage + 1} to{" "}
                    {Math.min(currentPage * perPage, totalRecords)} of{" "}
                    {totalRecords} records
                  </div>
                  <div>
                    <Pagination>
                      <Pagination.Prev
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                      />
                      {Array.from(
                        { length: Math.min(5, totalPages) },
                        (_, i) => {
                          let pageNum;
                          if (totalPages <= 5) {
                            pageNum = i + 1;
                          } else if (currentPage <= 3) {
                            pageNum = i + 1;
                          } else if (currentPage >= totalPages - 2) {
                            pageNum = totalPages - 4 + i;
                          } else {
                            pageNum = currentPage - 2 + i;
                          }

                          return (
                            <Pagination.Item
                              key={pageNum}
                              active={pageNum === currentPage}
                              onClick={() => handlePageChange(pageNum)}
                            >
                              {pageNum}
                            </Pagination.Item>
                          );
                        }
                      )}
                      <Pagination.Next
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                      />
                    </Pagination>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Footer Copyright */}
          <p
            style={{
              textAlign: "center",
              color: "#94a3b8",
              fontSize: "12.5px",
              marginTop: "24px",
              fontWeight: "500",
            }}
          >
            © {new Date().getFullYear()} A2 Demo Real Estates Pvt Ltd. All
            Rights Reserved.
          </p>
        </div>
      </div>

      {/* 🎯 MESSAGE MODAL */}
      {showMessageModal && (
        <div
          className="modal d-block"
          tabIndex="-1"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div
              className={`modal-content ${
                messageModalContent.type === "success"
                  ? "border-success"
                  : "border-danger"
              }`}
            >
              <div className="modal-header">
                <h5
                  className={`modal-title ${
                    messageModalContent.type === "success"
                      ? "text-success"
                      : "text-danger"
                  }`}
                >
                  {messageModalContent.title}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={closeCustomMessageModal}
                ></button>
              </div>
              <div className="modal-body">
                <p>{messageModalContent.text}</p>
              </div>
              <div className="modal-footer">
                <Button
                  variant={
                    messageModalContent.type === "success"
                      ? "success"
                      : "danger"
                  }
                  onClick={closeCustomMessageModal}
                >
                  OK
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
  

}

export default AssociatesBimaAchieverLists;